export function maybeParse(parse, val) {
    if (parse === undefined) {
        return val;
    }
    return parse(val);
}
/**
 * Generates strongly-typed CRUD-style functions for interacting with Reflect
 * from an Entity.
 */
export function generate(prefix, parse = undefined, logger = console) {
    const keyFromEntity = (_tx, entity) => key(prefix, entity.id);
    const keyFromID = id => key(prefix, id);
    const keyToID = (key) => id(prefix, key);
    const idFromEntity = (_tx, entity) => entity.id;
    const firstKey = () => key(prefix, '');
    const parseInternal = (_, val) => maybeParse(parse, val);
    const set = (tx, value) => setImpl(keyFromEntity, parseInternal, tx, value);
    return {
        set,
        put: set,
        init: (tx, value) => initImpl(keyFromEntity, parseInternal, tx, value),
        update: (tx, update) => updateImpl(keyFromEntity, idFromEntity, parseInternal, parseInternal, tx, update, logger),
        delete: (tx, id) => deleteImpl(keyFromID, noop, tx, id),
        has: (tx, id) => hasImpl(keyFromID, tx, id),
        get: (tx, id) => getImpl(keyFromID, parseInternal, tx, id),
        mustGet: (tx, id) => mustGetImpl(keyFromID, parseInternal, tx, id),
        list: (tx, options) => listImpl(keyFromID, keyToID, firstKey, parse, tx, options),
        listIDs: (tx, options) => listIDsImpl(keyFromID, keyToID, firstKey, tx, options),
        listEntries: (tx, options) => listEntriesImpl(keyFromID, keyToID, firstKey, parse, tx, options),
    };
}
function key(prefix, id) {
    return `${prefix}/${id}`;
}
function id(prefix, key) {
    return key.substring(prefix.length + 1);
}
export async function initImpl(keyFunc, parse, tx, initial) {
    const val = parse(tx, initial);
    const k = keyFunc(tx, val);
    if (await tx.has(k)) {
        return false;
    }
    await tx.set(k, val);
    return true;
}
export async function setImpl(keyFromEntity, parse, tx, initial) {
    const val = parse(tx, initial);
    await tx.set(keyFromEntity(tx, val), val);
}
export function hasImpl(keyFromID, tx, id) {
    return tx.has(keyFromID(id));
}
export function getImpl(keyFromID, parse, tx, id) {
    return getInternal(parse, tx, keyFromID(id));
}
export async function mustGetImpl(keyFromID, parse, tx, id) {
    const v = await getInternal(parse, tx, keyFromID(id));
    if (v === undefined) {
        throw new Error(`no such entity ${JSON.stringify(id)}`);
    }
    return v;
}
export async function updateImpl(keyFromEntity, idFromEntity, parseExisting, parseNew, tx, update, logger) {
    const k = keyFromEntity(tx, update);
    const prev = await getInternal(parseExisting, tx, k);
    if (prev === undefined) {
        const id = idFromEntity(tx, update);
        logger.debug?.(`no such entity ${JSON.stringify(id)}, skipping update`);
        return;
    }
    const next = { ...prev, ...update };
    const parsed = parseNew(tx, next);
    await tx.set(k, parsed);
}
export async function deleteImpl(keyFromLookupID, validateMutate, tx, id) {
    validateMutate(tx, id);
    await tx.del(keyFromLookupID(id));
}
export async function* scan(keyFromLookupID, keyToID, firstKey, tx, options) {
    const { startAtID, limit } = options ?? {};
    const fk = firstKey();
    for await (const [k, v] of tx
        .scan({
        prefix: fk,
        start: {
            key: startAtID === undefined ? fk : keyFromLookupID(startAtID),
        },
        limit,
    })
        .entries()) {
        const id = keyToID(k);
        if (id !== undefined) {
            yield [id, v];
        }
    }
}
export async function listImpl(keyFromID, keyToID, firstKey, parse, tx, options) {
    const result = [];
    for await (const [, v] of scan(keyFromID, keyToID, firstKey, tx, options)) {
        result.push(maybeParse(parse, v));
    }
    return result;
}
export async function listIDsImpl(keyFromID, keyToID, firstKey, tx, options) {
    const result = [];
    for await (const [k] of scan(keyFromID, keyToID, firstKey, tx, options)) {
        result.push(k);
    }
    return result;
}
export async function listEntriesImpl(keyFromID, keyToID, firstKey, parse, tx, options) {
    const result = [];
    for await (const [k, v] of scan(keyFromID, keyToID, firstKey, tx, options)) {
        result.push([k, maybeParse(parse, v)]);
    }
    return result;
}
async function getInternal(parse, tx, key) {
    const val = await tx.get(key);
    if (val === undefined) {
        return val;
    }
    return parse(tx, val);
}
function noop() {
    // intentionally empty
}
//# sourceMappingURL=generate.js.map